import React, { useState, useEffect } from 'react';
import { companiesAPI, emailAPI } from '../services/api';
import styles from './Settings.module.css';

export default function Settings() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [status, setStatus] = useState(null);

  // Form state for new company
  const [newCompany, setNewCompany] = useState({
    name: '',
    email_recipient: '',
    has_split_rates: false,
    standard_rate: '',
    advanced_rate: '',
  });

  // Form state for editing
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email_recipient: '',
    has_split_rates: false,
    standard_rate: '',
    advanced_rate: '',
  });

  useEffect(() => {
    loadCompanies();
    loadStatus();
    loadEmailHistory();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await companiesAPI.getAll();
      setCompanies(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try {
      const response = await emailAPI.getStatus();
      setStatus(response.data);
    } catch (err) {
      console.error('Error loading status:', err);
    }
  };

  const loadEmailHistory = async () => {
    try {
      const response = await emailAPI.getHistory(10);
      setEmailHistory(response.data);
    } catch (err) {
      console.error('Error loading email history:', err);
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.name || !newCompany.email_recipient) {
      setError('Please fill in all fields');
      return;
    }

    try {
      await companiesAPI.create(newCompany);
      setMessage('Company added successfully!');
      setNewCompany({ name: '', email_recipient: '', has_split_rates: false, standard_rate: '', advanced_rate: '' });
      loadCompanies();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add company');
    }
  };

  const handleEditCompany = (company) => {
    setEditingId(company.id);
    setEditForm({
      name: company.name,
      email_recipient: company.email_recipient,
      has_split_rates: !!company.has_split_rates,
      standard_rate: company.standard_rate || '',
      advanced_rate: company.advanced_rate || '',
    });
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      await companiesAPI.update(editingId, editForm);
      setMessage('Company updated successfully!');
      setEditingId(null);
      loadCompanies();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update company');
    }
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm('Are you sure? You cannot delete if there are entries.')) return;

    try {
      await companiesAPI.delete(id);
      setMessage('Company deleted successfully!');
      loadCompanies();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete company');
    }
  };

  return (
    <div className={styles.container}>
      <h1>Settings</h1>

      {error && <div className={styles.error}>{error}</div>}
      {message && <div className={styles.success}>{message}</div>}

      {/* System Status */}
      {status && (
        <div className={styles.statusSection}>
          <h2>System Status</h2>
          <div className={styles.statusGrid}>
            <div className={styles.statusCard}>
              <label>Status:</label>
              <p>{status.status}</p>
            </div>
            <div className={styles.statusCard}>
              <label>Scheduler:</label>
              <p>{status.schedulerStatus}</p>
            </div>
            <div className={styles.statusCard}>
              <label>Email Frequency:</label>
              <p>{status.cronPattern}</p>
            </div>
            <div className={styles.statusCard}>
              <label>Total Companies:</label>
              <p>{status.stats?.companies || 0}</p>
            </div>
            <div className={styles.statusCard}>
              <label>Total Entries:</label>
              <p>{status.stats?.timeEntries || 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* Add Company Form */}
      <div className={styles.formSection}>
        <h2>Add New Company</h2>
        <form onSubmit={handleAddCompany} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="company-name">Company Name:</label>
            <input
              type="text"
              id="company-name"
              value={newCompany.name}
              onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
              placeholder="e.g., Smith & Co"
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="company-email">Email Recipient:</label>
            <input
              type="email"
              id="company-email"
              value={newCompany.email_recipient}
              onChange={(e) => setNewCompany({ ...newCompany, email_recipient: e.target.value })}
              placeholder="payroll@smithco.com"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={newCompany.has_split_rates}
                onChange={(e) => setNewCompany({ ...newCompany, has_split_rates: e.target.checked })}
              />
              Split billing rates (Standard / Advanced)
            </label>
          </div>
          <div className={styles.ratesRow}>
            <div className={styles.formGroup}>
              <label htmlFor="company-std-rate">Standard Rate ($/hr):</label>
              <input
                type="number"
                id="company-std-rate"
                min="0"
                step="0.01"
                value={newCompany.standard_rate}
                onChange={(e) => setNewCompany({ ...newCompany, standard_rate: e.target.value })}
                placeholder="0.00"
              />
            </div>
            {newCompany.has_split_rates && (
              <div className={styles.formGroup}>
                <label htmlFor="company-adv-rate">Advanced Rate ($/hr):</label>
                <input
                  type="number"
                  id="company-adv-rate"
                  min="0"
                  step="0.01"
                  value={newCompany.advanced_rate}
                  onChange={(e) => setNewCompany({ ...newCompany, advanced_rate: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            )}
          </div>
          <button type="submit" className={styles.btnPrimary}>
            Add Company
          </button>
        </form>
      </div>

      {/* Companies List */}
      <div className={styles.companiesSection}>
        <h2>Manage Companies</h2>
        {loading ? (
          <p>Loading companies...</p>
        ) : companies.length === 0 ? (
          <p>No companies added yet.</p>
        ) : (
          <div className={styles.companiesList}>
            {companies.map((company) =>
              editingId === company.id ? (
                <form
                  key={company.id}
                  onSubmit={handleUpdateCompany}
                  className={styles.companyCard + ' ' + styles.editing}
                >
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                  <input
                    type="email"
                    value={editForm.email_recipient}
                    onChange={(e) => setEditForm({ ...editForm, email_recipient: e.target.value })}
                  />
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={editForm.has_split_rates}
                      onChange={(e) => setEditForm({ ...editForm, has_split_rates: e.target.checked })}
                    />
                    Split billing rates
                  </label>
                  <div className={styles.ratesRow}>
                    <div className={styles.formGroup}>
                      <label>Std Rate ($/hr):</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editForm.standard_rate}
                        onChange={(e) => setEditForm({ ...editForm, standard_rate: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                    {editForm.has_split_rates && (
                      <div className={styles.formGroup}>
                        <label>Adv Rate ($/hr):</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editForm.advanced_rate}
                          onChange={(e) => setEditForm({ ...editForm, advanced_rate: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                    )}
                  </div>
                  <div className={styles.editActions}>
                    <button type="submit" className={styles.btnSmallSuccess}>
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className={styles.btnSmallSecondary}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div key={company.id} className={styles.companyCard}>
                  <div className={styles.companyInfo}>
                    <strong>{company.name}</strong>
                    {!!company.has_split_rates && (
                      <span className={styles.splitBadge}>Split Rates</span>
                    )}
                    <p>{company.email_recipient}</p>
                    {(company.standard_rate > 0 || company.advanced_rate > 0) && (
                      <p className={styles.rateInfo}>
                        Std: ${company.standard_rate || 0}/hr
                        {!!company.has_split_rates && ` · Adv: $${company.advanced_rate || 0}/hr`}
                      </p>
                    )}
                  </div>
                  <div className={styles.companyActions}>
                    <button
                      onClick={() => handleEditCompany(company)}
                      className={styles.btnSmallSecondary}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCompany(company.id)}
                      className={styles.btnSmallDelete}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Email History */}
      <div className={styles.historySection}>
        <h2>Recent Email Sends</h2>
        {emailHistory.length === 0 ? (
          <p>No email history yet.</p>
        ) : (
          <div className={styles.historyTable}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Recipient</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {emailHistory.map((entry) => (
                  <tr key={entry.id}>
                    <td>{new Date(entry.sent_at).toLocaleString()}</td>
                    <td>{entry.recipient_email}</td>
                    <td className={styles[entry.status]}>
                      {entry.status.toUpperCase()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
